/**
 * Phase 32-04: normalise the raw HubSpot ticket.location enum value to a
 * display-ready { label, resolvedNote } pair for the JobDetailPanel
 * source-label chip. Pure function — no React, no API access, no I/O.
 *
 * The 4 valid HubSpot enum values per project_ticket_location_enum.md:
 *   - 'G2'                       → fixed app_config address
 *   - 'HQ'                       → fixed app_config address
 *   - 'Client Site Address'      → linked deal's site_address
 *   - 'Employees Home Address'   → per-worker, resolved at clock-in by
 *     (or "Employee's Home Address")  Time PWA against Xero home_address
 *                                     (Phase 33 work)
 *
 * Returns null when the input is null/undefined/empty — caller renders no chip.
 * Unknown values are surfaced verbatim (trimmed) with no resolvedNote, so a
 * future HubSpot-side enum addition doesn't blank the panel — graceful fallback.
 */

export interface LocationSourceDisplay {
  /** Display label for the source-label chip. */
  label: string;
  /**
   * Non-null only for the Employee Address variant. The caller renders this
   * in place of a resolved address (until Phase 33 wires per-worker Xero
   * home_address resolution at clock-in time).
   */
  resolvedNote: string | null;
}

export function formatLocationSource(raw: string | null | undefined): LocationSourceDisplay | null {
  if (raw == null) return null;
  const trimmed = raw.trim();
  if (trimmed === '') return null;

  const normalised = trimmed.toLowerCase();
  if (normalised === 'g2') return { label: 'G2', resolvedNote: null };
  if (normalised === 'hq') return { label: 'HQ', resolvedNote: null };
  if (normalised === 'client site address') {
    return { label: 'Client Site Address', resolvedNote: null };
  }
  if (normalised === 'employees home address' || normalised === "employee's home address") {
    return {
      label: 'Employee Address',
      resolvedNote: 'Resolved at clock-in (per assigned worker)',
    };
  }

  return { label: trimmed, resolvedNote: null };
}
